import { useDispatch, useSelector } from 'react-redux';
import {
  SlideOutSheet,
  OnboardingChrome,
  ResultsAreInSlideOutContent,
  citations,
} from '../components';
import {
  putClientOnboardingSlideProgressByClientIdRequest,
  selectClient,
  selectClientOnboardingSlideProgress,
  selectSession,
} from '../model';
import advisorImageUrl from '../images/OnboardingResultsAreIn.png';
import { SlideContent } from '../components/SlideContent';
import { OnboardingOurPredictions, OnboardingCostFactors } from '../pages';
import { useEffect } from 'react';
import { SessionType, newUuid } from '@shared';
import { CLIENT_STARTED_REVIEW } from '..';

export function OnboardingResultsAreIn({ clientId }: ClientIdProps) {
  const client = useSelector(selectClient)!;
  const {
    clientFirstName,
    intakeSurvey: { clientHasStartedLtc },
  } = client;

  const dispatch = useDispatch();

  const onboardingSlideProgress = useSelector(
    selectClientOnboardingSlideProgress,
  );

  const { sessionType } = useSelector(selectSession);

  useEffect(() => {
    if (
      (onboardingSlideProgress &&
        onboardingSlideProgress.hasClientStartedOnboarding) ||
      sessionType !== SessionType.client
    ) {
      return;
    }
    const clientOnboardingSlideProgressId =
      onboardingSlideProgress?.clientOnboardingSlideProgressId ?? newUuid();
    const updateClientTags = [...client.clientTags, CLIENT_STARTED_REVIEW];
    dispatch(
      putClientOnboardingSlideProgressByClientIdRequest({
        clientOnboardingSlideProgressId,
        clientId: clientId ?? client.clientId,
        hasClientStartedOnboarding: true,
        clientTags: updateClientTags,
      }),
    );
  }, [
    dispatch,
    clientId,
    onboardingSlideProgress?.hasClientStartedOnboarding,
    onboardingSlideProgress?.clientOnboardingSlideProgressId,
  ]);

  return (
    <OnboardingChrome
      slidePage={OnboardingResultsAreIn}
      nextButtonLabel={
        clientHasStartedLtc
          ? `See ${clientFirstName}'s Costs`
          : `Check Out ${clientFirstName}'s Numbers`
      }
      nextSlidePage={
        clientHasStartedLtc ? OnboardingCostFactors : OnboardingOurPredictions
      }
      citations={[citations.OnboardingResultsAreInDisclaimer]}
    >
      <SlideContent headerText="">
        <div className="mt-1 grid gap-y-2  sm:grid-cols-1 md:mt-8 md:gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-2">
          <div className="lg:max-h-12">
            <img
              src={advisorImageUrl}
              className="mx-0 md:mx-auto max-w-[8rem] md:max-w-[22em]"
              alt="Results are in"
            />
          </div>
          <div className="flex flex-col gap-3 mb-4 leading-normal tracking-wide md:gap-5">
            <h2
              className="text-lg font-bold md:px-0 md:text-4xl"
              data-testid="results-are-in-header"
            >
              {clientFirstName}'s results are in!
            </h2>
            <p
              className="text-sm md:text-xl"
              data-testid="results-are-in-description"
            >
              {clientFirstName}&apos;s long-term care predictions that follow are made using{' '}
              <span className="text-primary ">artificial intelligence (AI)</span> by comparing{' '}
              {clientFirstName}&apos;s information with similar data from nearly{' '}
              <span className="text-primary">50,000</span> families.
            </p>
            <p className="text-sm md:text-xl">
              We draw from a dataset with over{' '}
              <span className="text-primary">500,000,000</span> data points to
              personalize {clientFirstName}&apos;s predictions.{' '}
              <SlideOutSheet
                slideOutContent={
                  <ResultsAreInSlideOutContent
                    clientFirstName={clientFirstName}
                  />
                }
                title="FAQ"
              >
                <span className="ml-1 w-full text-left font-normal text-primary underline hover:text-primary-hover">
                  Learn more
                </span>
              </SlideOutSheet>{' '}
              about our data and methodology.
            </p>
            <div className="flex-grow"></div>
          </div>
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}
